/* global fetch */

/**
 * Connects to Azure function "vpiapi" and gets AUM from DB
 * @customfunction
 * @return AUM as a floating point
 */
export async function getAUM(inputDate) {
  try {
    var dateType = dateFormatChecker(inputDate);
    var date;
    var result = "";
    switch (dateType) {
      case "string":
        date = inputDate;
        break;
      case "cell":
        date = ExcelDateToJSDate(inputDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid date";
    }

    const content = {
      date: date,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    //const url = "https://vpiapi.azurewebsites.net/api/v2/company/getAUM";
    //const url = "https://lemon-meadow-030977210.2.azurestaticapps.net/api/v2/company/getAUM";
    //const url = "/api/v2/company/getAUM";
    const url = "/api/v2/company/getAUM";
    //const url = "http://localhost:7286/api/getAUM";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    result = jsonResponse.Aum;
    //result = jsonResponse.Aum;

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets Fund price of specified fund, series, and date from DB
 * @customfunction
 * @param {string} fund String to write.
 * @param {string} series String to write.
 * @param date
 * @return {string} fund price + date formatted as a string
 */
export async function getFundPrice(fund, series, inputDate) {
  try {
    var dateType = dateFormatChecker(inputDate);
    var date;
    switch (dateType) {
      case "string":
        date = inputDate;
        break;
      case "cell":
        date = ExcelDateToJSDate(inputDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid date";
    }

    var result = "";
    const content = {
      format: "false",
      fund: fund,
      series: series,
      date: date,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    const url = "/api/v2/fund/getFundPrice";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      const textResponse = await response.text();
      result = textResponse.toString();
    } else {
      const jsonResponse = await response.json(); //use .JSON if format = false and .text if format = true
      result = parseFloat(jsonResponse.Unit_Price);
    }
    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets Fund data of specified fund, and date from DB
 * @customfunction
 * @param {string} fundcode String to write.
 * @param {string} columnname String to write.
 * @param date
 * @return {string} specified column from the fund data table
 */
export async function getFundCodeData(fundcode, columnname, inputDate) {
  try {
    var columnNameIndex = columnname.toLowerCase();
    var dateType = dateFormatChecker(inputDate);
    var date;
    switch (dateType) {
      case "string":
        date = inputDate;
        break;
      case "cell":
        date = ExcelDateToJSDate(inputDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid date";
    }

    var result = "";
    const content = {
      fundCode: fundcode,
      column: columnNameIndex,
      date: date,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    const url = "/api/v2/fund/getFundCodeData";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      const textResponse = await response.text();
      result = textResponse.toString();
    } else {
      const jsonResponse = await response.json(); //use .JSON if format = false and .text if format = true
      result = jsonResponse[columnNameIndex];
    }
    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets the Fund code of specified fund from DB
 * @customfunction
 * @param {string} fund String to write.
 * @param {string} series String to write.
 * @return {string} specified column from the fund data table
 */
export async function getFundCode(fund, series) {
  try {
    var result = "";
    const content = {
      fund: fund,
      series: series,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    const url = "/api/v2/fund/getFundCode";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      const textResponse = await response.text();
      result = textResponse.toString();
    } else {
      const jsonResponse = await response.json(); //use .JSON if format = false and .text if format = true
      result = jsonResponse.fund_code;
    }
    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets General Return %
 * @customfunction
 * @param  startDate
 * @param  endDate
 * @param {string} fundCode
 * @return {string} Return General Return % as String
 */
export async function getGeneralReturn(inputStartDate, inputEndDate, fundCode) {
  try {
    var startDate;
    var endDate;

    //Check startDate Format
    var startDateType = dateFormatChecker(inputStartDate);
    switch (startDateType) {
      case "string":
        startDate = inputStartDate;
        break;
      case "cell":
        startDate = ExcelDateToJSDate(inputStartDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "(START DATE) Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid start date";
    }

    //Check endDate format
    var endDateType = dateFormatChecker(inputEndDate);
    switch (endDateType) {
      case "string":
        endDate = inputEndDate;
        break;
      case "cell":
        endDate = ExcelDateToJSDate(inputEndDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "(END DATE) Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid end date";
    }

    var result = 0;
    const content = {
      startDate: startDate,
      endDate: endDate,
      fundCode: fundCode,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/fund/getGeneralReturn";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.General_Return;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets Net Sales
 * @customfunction
 * @param  period can be Yearly, Monthly, or Daily
 * @return Return net sales
 */
export async function getNetSales(period) {
  try {
    var result = 0;
    const content = {
      period: period,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/company/getNetSales";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.NetSales;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets Share price
 * @customfunction
 * @param inputDate
 * @return  Return share price
 */
export async function getSharePrice(inputDate) {
  try {
    var dateType = dateFormatChecker(inputDate);
    var date;
    switch (dateType) {
      case "string":
        date = inputDate;
        break;
      case "cell":
        date = ExcelDateToJSDate(inputDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid date";
    }

    var result = 0;
    const content = {
      format: false,
      date: date,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/company/getSharePrice";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.SharePrice;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * get perfomance rate for one of the following periods "1 year, 3 year, 5 year, 10 year, inception.""
 * @customfunction
 * @param  fund
 * @param returnPeriod test
 * @return Return net sales
 */
export async function getFundPerformance(fund, returnPeriod) {
  try {
    var result = 0;
    const content = {
      fund: fund,
      returnPeriod: returnPeriod,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/fund/getFundPerformance";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.Rate;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Connects to Azure function "vpiapi" and gets MER History
 * @customfunction
 * @param inputDate
 * @return  Return share price
 */
export async function getMERHistory(inputDate) {
  try {
    var dateType = dateFormatChecker(inputDate);
    var date;
    switch (dateType) {
      case "string":
        date = inputDate;
        break;
      case "cell":
        date = ExcelDateToJSDate(inputDate, -18); //-18 for Central Time
        break;
      case "invalid":
        return "Please wrap your date in quotations, or supply a date from a cell.";
      default:
        return "Invalid date";
    }

    var result = 0;
    const content = {
      date: date,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/company/getMERHistory";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.MER_Rate;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

/**
 * Enter a fund code (i.e. 001) and return either the 'short' or 'long' fund name
 * @customfunction
 * @param fundCode
 * @param length
 * @return  Return share price
 */
export async function getFundName(fundCode, length) {
  try {
    var result = 0;
    const content = {
      fundCode: fundCode,
      length: length,
    };
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(content),
    };

    //You can change this URL to any web request you want to work with.
    const url = "/api/v2/fund/GetFundName";
    const response = await fetch(url, options);
    //Expect that status code is in 200-299 range
    if (!response.ok) {
      throw new Error("Bad Response");
    }
    const jsonResponse = await response.json(); //use .JSON if not formatted
    //result = parseFloat(jsonResponse.Aum);
    result = jsonResponse.FundName;
    //result = result + "%";

    return result;
  } catch (error) {
    return error;
  }
}

function ExcelDateToJSDate(serialDate, offsetUTC) {
  //UTC Offset is -18 to get to central time, otherwise dates will be off
  var date = new Date(Date.UTC(0, 0, serialDate, offsetUTC));
  var dateOnly = date.toISOString().split("T")[0];
  return dateOnly;
}

//Implementation of (if date > 36000)
//Excel sends in dates as "Days since 1990".
//When you enter a date into an excel cell it will automatically format it this way (so 2020-01-04 == 43834 in excel format).
//If a date is entered into the function parameters without coming from a cell or being wrapped in quotations, this function will handle the parameter improperly
//For example: 2000-01-01  will be handled as if it were already in excel format
//All dates prior to 2000-01-01 will evaluate to an excel format lower than "36000", this if check ensures we are only accepting dates from cells or dates wrapped in quotes
function dateFormatChecker(inputdate) {
  var result = "";

  if (typeof inputdate == "string") {
    result = "string";
  } else if (inputdate > 36000) {
    //Do not change this number untill reading the above description
    result = "cell";
  } else {
    result = "invalid";
  }

  return result;
}

// /**
//  * This function uses a regEx to test the format of the given date for the following: YYYY - MM - DD
//  * @param dateString a string to test
//  * @return true if the date is valid, false otherwise
//  */
// function isValidDate (dateString) {
//   var regEx = /^\d{4}-\d{2}-\d{2}$/;
//   if (!dateString.match(regEx)) return false; // Invalid format
//   var date = new Date(dateString);
//   var dNum = date.getTime();
//   if (!dNum && dNum !== 0) return false; // NaN value, Invalid date
//   return date.toISOString().slice(0, 10) === dateString;
// }

CustomFunctions.associate("GETAUM", getAUM);
CustomFunctions.associate("GETFUNDPRICE", getFundPrice);
CustomFunctions.associate("GETFUNDCODEDATA", getFundCodeData);
CustomFunctions.associate("GETFUNDCODE", getFundCode);
CustomFunctions.associate("GETGENERALRETURN", getGeneralReturn);
CustomFunctions.associate("GETNETSALES", getNetSales);
CustomFunctions.associate("GETSHAREPRICE", getSharePrice);
CustomFunctions.associate("GETFUNDPERFORMANCE", getFundPerformance);
CustomFunctions.associate("GETMERHISTORY", getMERHistory);
CustomFunctions.associate("GETFUNDNAME", getFundName);